import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Box, Grid, Typography, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import SplitPaymentCollector from '../../components/SplitPaymentCollector';
import ReceiptView from '../../components/ReceiptView';
import PharmacyTableState from './PharmacyTableState';
import { getControlledQueue } from '../../api/pharmacy';
import { getSale, getSaleItems, deleteSale } from '../../api/pharmacyDispense';
import { toInventoryList } from '../../api/inventory-request';
import { mapList, mapControlledItem } from '../../utils/pharmacyMappers';
import { rollupControlledStatus } from '../../utils/controlledDrug';
import { resolvePharmacyScope, getPharmacyActorName } from '../../hooks/usePharmacyRole';
import useItemNames from '../../hooks/useItemNames';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import useDirectDispensePipeline from './direct/useDirectDispensePipeline';
import { formatINR, formatDateTime, parseMoney } from '../../utils';
import { IPD_COLORS } from '../../theme';
import { fadeInSx } from './pharmacyStyles';

/**
 * A parked controlled order, reopened.
 *
 * Read-only by design: by the time an order reaches this screen the pharmacist
 * has ruled on it, and re-editing the basket would void those authorizations.
 * The counter's job here is to confirm and collect — or discard the order.
 */
export default function ControlledOrderDetail() {
  const { saleId } = useParams();
  const history = useHistory();
  const { url } = useRouteMatch();
  const listUrl = url.replace(/\/[^/]+$/, '');

  const pipeline = useDirectDispensePipeline();
  const { state } = pipeline;
  // Non-controlled lines carry no authorization row to borrow a name from, and
  // a cashier should never be shown a raw item uuid.
  const { resolveItemId } = useItemNames();

  const [sale, setSale] = useState(null);
  const [items, setItems] = useState([]);
  const [auths, setAuths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [lastModes, setLastModes] = useState([]);

  // Deliberately NOT derived from pipeline state. Every patch() that carries a
  // scope hands back a fresh object, which re-created load() and re-fetched the
  // whole order — three wasted requests per pipeline step, several times per
  // payment. The gateway stamps tenant/hospital from the session headers
  // regardless, so one stable value for the life of the screen is correct.
  const scope = useMemo(() => resolvePharmacyScope(), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [saleData, itemsData, authData] = await Promise.all([
        getSale(saleId, scope),
        getSaleItems(saleId, scope),
        getControlledQueue({ inventory_transaction_id: saleId, status: undefined }),
      ]);
      // inventory-request already unwraps the gateway envelope.
      setSale(saleData);
      setItems(toInventoryList(itemsData));
      setAuths(mapList(authData, mapControlledItem));
      setError(null);
      setRefreshedAt(new Date().toISOString());
    } catch (err) {
      setError(err?.message || 'Could not load this order');
    } finally {
      setLoading(false);
    }
  }, [saleId, scope]);

  useEffect(() => { load(); }, [load]);

  // Pick up a decision made in the pharmacist's tab without a timer.
  useRefreshOnFocus(load);

  const authByLineId = useMemo(() => {
    const map = {};
    auths.forEach((row) => {
      if (row.transaction_item_id) map[String(row.transaction_item_id)] = row;
    });
    return map;
  }, [auths]);

  const orderStatus = rollupControlledStatus(auths);
  const allAuthorized = auths.length > 0 && orderStatus === 'AUTHORIZED';
  const patientName = sale?.transaction_payload?.patient_name || 'Walk-in';
  const patientMobile = sale?.transaction_payload?.patient_mobile || '';
  const isDraft = String(sale?.status || '').toUpperCase() === 'DRAFT';

  const estimatedTotal = items.reduce((sum, i) => sum + parseMoney(i.line_total), 0);
  const orderSummary = useMemo(() => {
    let subtotal = 0;
    let discount = 0;
    items.forEach((item) => {
      const basic = parseMoney(item.line_basic_amount);
      const qty = parseMoney(item.requested_quantity);
      const rate = parseMoney(item.unit_rate);
      subtotal += basic > 0 ? basic : qty * rate;
      discount += parseMoney(item.line_discount_amount);
    });
    return {
      subtotal,
      discount,
      discountPct: subtotal > 0 ? (discount / subtotal) * 100 : 0,
    };
  }, [items]);
  const payableDisplay = state.amountPayable ?? estimatedTotal;
  const amountIsEstimated = state.amountPayable == null;
  const paid = state.billStatus === 'PAID' && state.paymentResult;

  // The receipt needs the same lines the table shows. The Direct flow builds
  // these from its in-memory basket; a resumed order has no basket, so they
  // come off the sale items instead. Totals are left for ReceiptView to derive
  // — line_basic_amount is not guaranteed on a sale line, and its qty x rate
  // fallback is right whichever way that goes.
  const receiptLines = useMemo(() => items.map((item) => ({
    id: item.id,
    name: authByLineId[String(item.id)]?.medicine || resolveItemId(item.item_id),
    batch_number: item.batch_number,
    expiry_date: item.expiry_date,
    qty: item.requested_quantity,
    rate: item.unit_rate,
    line_basic_amount: item.line_basic_amount,
    item_discount_amount: item.line_discount_amount,
    line_net_amount: item.line_total,
  })), [items, authByLineId, resolveItemId]);

  async function handleCollect(modes) {
    setBusy(true);
    setLastModes(modes);
    try {
      const prepared = await pipeline.runPrepareBill({
        lines: [],
        store: null,
        storeId: sale?.from_store_id,
        patientDisplay: { name: patientName, mobile: patientMobile },
        remarks: sale?.remarks || '',
        existingSaleId: saleId,
        // The order carries its own tenant/hospital; there is no store picker here.
        scope: { tenant_id: sale?.tenant_id, hospital_id: sale?.hospital_id },
      });
      const payable = parseMoney(prepared?.amountPayable ?? state.amountPayable);
      const allocated = (modes || []).reduce((sum, m) => sum + parseMoney(m.amount), 0);
      if (Math.abs(allocated - payable) > 0.05) {
        toast.error(
          `Bill priced at ₹ ${payable.toFixed(2)} but payment totals ₹ ${allocated.toFixed(2)}. Adjust and try again.`,
        );
        return;
      }
      await pipeline.collectPayment(modes);
      toast.success('Payment collected');
    } catch (err) {
      toast.error(err?.message || 'Could not collect payment');
    } finally {
      setBusy(false);
    }
  }

  async function handleCancelOrder() {
    setBusy(true);
    try {
      await deleteSale(saleId, scope);
      toast.success('Order cancelled — pending authorizations withdrawn');
      history.push(listUrl);
    } catch (err) {
      toast.error(err?.message || 'Could not cancel this order');
    } finally {
      setBusy(false);
    }
  }

  if (paid) {
    const receiptNumber = state.paymentResult?.receipt_number || state.paymentResult?.id;
    return (
      <Box sx={fadeInSx}>
        <SectionCard title="Order complete">
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: IPD_COLORS.success.text, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 0.5 }}>Payment successful</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 800, color: IPD_COLORS.primary, mb: 2 }}>
              {receiptNumber}
            </Typography>
            <ReceiptView
              receiptNumber={receiptNumber}
              billNumber={state.billId}
              patientLabel={patientName}
              lines={receiptLines}
              amountPayable={state.amountPayable}
              modes={lastModes}
              cashierName={state.paymentResult?.cashier_name || getPharmacyActorName()}
              createdAt={state.paymentResult?.created_at}
            />
            <Box sx={{ mt: 2 }}>
              <InvButton onClick={() => history.push(listUrl)}>Back to Controlled Orders</InvButton>
            </Box>
          </Box>
        </SectionCard>
      </Box>
    );
  }

  return (
    <Box sx={fadeInSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InvButton variant="outlined" tone="teal" size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
          Back
        </InvButton>
        <InvButton variant="outlined" tone="teal" size="small" startIcon={<RefreshIcon />} disabled={loading} onClick={load}>
          Refresh
        </InvButton>
      </Box>

      <PharmacyTableState loading={loading} error={error} empty={false} onRetry={load}>
        {!isDraft && sale && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This order is already {String(sale.status).toLowerCase()} — nothing further to collect.
          </Alert>
        )}
        {orderStatus === 'REJECTED' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            A pharmacist denied one or more medicines on this order. Cancel it and start a
            new dispense without the denied item.
          </Alert>
        )}
        {orderStatus === 'AWAITING_AUTHORIZATION' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Still waiting for a pharmacist to authorize this order in the Controlled Queue.
            Payment unlocks here once every controlled line is authorized.
          </Alert>
        )}

        <SectionCard
          title={sale?.transaction_number || 'Order'}
          subtitle={`${patientName}${patientMobile ? ` · ${patientMobile}` : ''}`}
          actions={<StatusChip status={orderStatus} />}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                  {['Medicine', 'Batch', 'Qty', 'Amount', 'Authorization'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, color: IPD_COLORS.textSecondary, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const auth = authByLineId[String(item.id)];
                  return (
                    <tr key={item.id} style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                      <td style={{ padding: '12px', fontWeight: 600 }}>
                        {auth?.medicine || resolveItemId(item.item_id)}
                      </td>
                      <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: 12 }}>
                        {item.batch_number || '—'}
                      </td>
                      <td style={{ padding: '12px' }}>{item.requested_quantity}</td>
                      <td style={{ padding: '12px' }}>{formatINR(item.line_total)}</td>
                      <td style={{ padding: '12px' }}>
                        {auth ? (
                          <>
                            <StatusChip status={auth.status} />
                            {auth.approved_by && (
                              <Box sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 0.5 }}>
                                by {auth.approved_by}
                                {auth.approved_at ? ` · ${formatDateTime(auth.approved_at)}` : ''}
                              </Box>
                            )}
                            {auth.status === 'REJECTED' && auth.reason && (
                              <Box sx={{ fontSize: 12, color: IPD_COLORS.error.text, mt: 0.5 }}>
                                {auth.reason}
                              </Box>
                            )}
                          </>
                        ) : (
                          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                            Not controlled
                          </Typography>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>

          <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 1.5 }}>
            {refreshedAt
              ? `Last refreshed ${formatDateTime(refreshedAt)} · refreshes when you return to this tab`
              : 'Refreshes when you return to this tab'}
          </Typography>
        </SectionCard>

        <Grid container spacing={2} sx={{ mt: 2 }} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <SectionCard title="Order Summary" sx={{ height: '100%' }}>
              <SummaryRow label="Subtotal" value={formatINR(orderSummary.subtotal)} />
              <SummaryRow
                label={orderSummary.discountPct > 0
                  ? `Discount (${Math.round(orderSummary.discountPct * 100) / 100}%)`
                  : 'Discount'}
                value={`− ${formatINR(orderSummary.discount)}`}
              />
              <Box sx={{ borderTop: `1px solid ${IPD_COLORS.border}`, my: 1 }} />
              <SummaryRow
                label={amountIsEstimated ? 'Estimated Amount Payable' : 'Amount Payable'}
                value={formatINR(payableDisplay)}
                accent
              />
              {amountIsEstimated && (
                <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.75 }}>
                  Final amount will be confirmed at payment.
                </Typography>
              )}
            </SectionCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionCard title="Payment & Dispense" sx={{ height: '100%' }}>
              <SplitPaymentCollector
                totalDue={payableDisplay}
                direction="collect"
                autoFill
                compact
                hideHeading
                remainingLabel="Amount Remaining"
                modeLabel="Payment Method"
                amountLabel="Amount to Collect"
                submitting={busy || state.pipelineBusy}
                disabled={!allAuthorized || !isDraft}
                onSubmit={handleCollect}
              />
            </SectionCard>
          </Grid>
          <Grid item xs={12}>
            <SectionCard title="Discard" subtitle="Withdraws this order and its pending authorizations">
              <InvButton
                tone="red"
                startIcon={<DeleteIcon />}
                disabled={busy || !isDraft}
                onClick={handleCancelOrder}
              >
                Cancel order
              </InvButton>
            </SectionCard>
          </Grid>
        </Grid>
      </PharmacyTableState>
    </Box>
  );
}

function SummaryRow({ label, value, accent = false }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.4 }}>
      <Typography sx={{
        fontSize: accent ? 13 : 12,
        fontWeight: accent ? 700 : 500,
        color: IPD_COLORS.textPrimary,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: accent ? 16 : 13,
        fontWeight: accent ? 800 : 600,
        fontVariantNumeric: 'tabular-nums',
        color: accent ? IPD_COLORS.primary : IPD_COLORS.textPrimary,
      }}>
        {value}
      </Typography>
    </Box>
  );
}
